import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BusinessIcon from "@mui/icons-material/Business";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import LogoPdf from "../../../../src/assets/LogoPdf.png";
import { useEffect, useState } from "react";
import { formatDateString } from "../../../helpers"
import { Paper } from "@mui/material";
import EgressLabelPrinter from "../createEgress/labelPrinterEgress";
import integrationService from "../../../services/integrationService";
import {
  warehouseRemitenteLines,
  warehouseRemitenteOneLine,
} from "../../../utils/remitente";

// Detección ML (mismo criterio que el listado): solo ME2/Flex tienen etiqueta del canal;
// el resto imprime la etiqueta de bulto. Duplicado acotado para no acoplar con index.js.
const ML_SHIPPING_MODES = { me_flex: true, me2: true };
const isMlShippingMode = (m) => !!ML_SHIPPING_MODES[String(m || "").toLowerCase()];
const isMercadoLibreIntegration = (mov) => {
  const name = mov?.integration?.name;
  if (!name || typeof name !== "string") return false;
  const n = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return n.includes("mercado libre") || n.includes("mercadolibre");
};

// Grupo de datos: un solo ícono + título; los campos van debajo, sin ícono propio.
const DetailGroup = ({ icon, title, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 26,
          height: 26,
          borderRadius: "7px",
          bgcolor: "#E8F0FE",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 15, color: "primary.main" } })}
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: "#475569",
          letterSpacing: "0.06em",
          fontSize: "0.7rem",
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box sx={{ pl: "34px" }}>{children}</Box>
  </Box>
);

const DetailField = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 1.5, mb: 0.4, alignItems: "baseline" }}>
    <Typography sx={{ color: "#94a3b8", fontSize: "0.72rem", minWidth: 110, flexShrink: 0, lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#1e293b", lineHeight: 1.4, wordBreak: "break-word", minWidth: 0 }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

// Etiqueta legible de la operativa (mismo criterio que la etiqueta de bulto).
const OPERATIVA_LABELS = {
  cliente: "Cliente retira",
  retiro_puerta: "Cliente retira",
  red_despacho: "Despacho",
  redespacho: "Redespacho",
  transporte_manual: "Transporte retira",
  me_flex: "MercadoEnvíos Flex",
  me2: "MercadoEnvíos (ME2)",
};
const formatOperativa = (method) => {
  if (!method) return "No definido";
  return OPERATIVA_LABELS[String(method).toLowerCase()] || String(method).replace(/_/g, " ");
};

// Canal de venta (de dónde vino el egreso) + nº de orden del canal.
const CHANNEL_LABELS = {
  mercadolibre: "MercadoLibre",
  tiendanube: "TiendaNube",
  woocommerce: "WooCommerce",
};
// Seguimiento del envío del canal (ML): estado vivo que mantiene el webhook.
const trackingLabel = (status, substatus) => {
  if (!status) return null;
  const sub = (substatus || "").toLowerCase();
  switch ((status || "").toLowerCase()) {
    case "pending":
    case "handling":
      return "⏳ ML preparando envío";
    case "ready_to_ship":
      return sub === "printed" ? "🖨 Etiqueta impresa" : "🏷 Etiqueta lista";
    case "shipped":
      return sub === "out_for_delivery" ? "📍 En reparto" : "🚚 En camino";
    case "delivered":
      return "✅ Entregado";
    case "not_delivered":
    case "undelivered":
    case "returned":
      return "⚠️ Problema de entrega";
    case "cancelled":
      return "✖ Envío cancelado";
    default:
      return status;
  }
};

const formatChannel = (name) =>
  CHANNEL_LABELS[String(name || "").toLowerCase()] || (name ? String(name) : "Manual");
// Nº de orden del canal: preferimos el número "lindo" (TN #9929); si no, el id del canal.
const formatSaleRef = (integration) => {
  if (!integration) return null;
  const num = integration.transaction_number;
  if (num && num !== "0") return `#${num}`;
  return integration.transaction_id ? `#${integration.transaction_id}` : null;
};

const ModalView = ({ ingressDetail, open, onClose, onLabelPrinted }) => {
  useEffect(() => { }, [ingressDetail]);
  const [labelsToPrint, setLabelsToPrint] = useState([]);

  const parseLogistics = (logisticsValue) => {
    if (!logisticsValue) return {};
    if (typeof logisticsValue === "string") {
      try {
        return JSON.parse(logisticsValue);
      } catch {
        return {};
      }
    }
    return logisticsValue;
  };

  // Extraer información de logística y bultos
  const logistics = parseLogistics(ingressDetail?.logistics);
  const deliveryDetails = logistics?.deliveryDetails || logistics?.delivery_details || {};
  const shippingMethod = logistics?.shippingMethod || null;
  const packages = ingressDetail?.packages || [];
  const totalBultos =
    typeof ingressDetail?.package_count === "number" && ingressDetail.package_count > 0
      ? ingressDetail.package_count
      : Array.isArray(packages)
        ? packages.length
        : 0;

  // Formatear tipo de retiro para mostrar
  const formatShippingMethod = (method) => {
    if (!method) return "No definido";
    const ml = { me_flex: "MercadoEnvíos Flex", me2: "MercadoEnvíos (ME2)" };
    if (ml[String(method).toLowerCase()]) return ml[String(method).toLowerCase()];
    return method
      .replace(/_/g, " ")
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const buildThirdPartyCarrier = (dd) => {
    const source = dd?.thirdPartyCarrier || dd?.third_party_carrier;
    if (!source) return undefined;
    const mapLeg = (leg) => ({
      companyName: leg?.companyName ?? leg?.company_name ?? "",
      licensePlate: leg?.licensePlate ?? leg?.license_plate ?? "",
      phone: leg?.phone ?? leg?.telefono ?? "",
    });
    const out = {
      intermediate: mapLeg(source.intermediate),
      final: mapLeg(source.final),
    };
    const hasAny =
      out.intermediate.companyName ||
      out.intermediate.licensePlate ||
      out.intermediate.phone ||
      out.final.companyName ||
      out.final.licensePlate ||
      out.final.phone;
    return hasAny ? out : undefined;
  };

  const handlePrintLabel = () => {
    const packagesList = Array.isArray(ingressDetail?.packages) ? ingressDetail.packages : [];
    const total =
      (typeof ingressDetail?.package_count === "number" && ingressDetail.package_count > 0
        ? ingressDetail.package_count
        : typeof ingressDetail?.bultos === "number" && ingressDetail.bultos > 0
          ? ingressDetail.bultos
          : packagesList.length) || 0;

    if (!total || !ingressDetail?.id) return;

    const dd = logistics?.deliveryDetails || logistics?.delivery_details || {};
    const thirdPartyCarrier = buildThirdPartyCarrier(dd);
    const clientName = ingressDetail.provider_name || "";
    const remitente = warehouseRemitenteOneLine(ingressDetail);
    const labels = Array.from({ length: total }, (_, index) => {
      const pkg = packagesList[index] ?? packagesList[0] ?? {};
      const movementDetail =
        ingressDetail?.movement_details?.[index] ||
        ingressDetail?.movement_details?.[0] ||
        {};
      return {
        index: pkg.index || index + 1,
        total,
        orderId: ingressDetail.id,
        clientName,
        shippingMethod: logistics?.shippingMethod || null,
        ...(thirdPartyCarrier ? { thirdPartyCarrier } : {}),
        destinatario: {
          nombre: dd.receiverName || clientName,
          dni: dd.receiverId || "",
          telefono: dd.contact?.phone || ingressDetail.phone_number || "",
          direccion: dd.address?.street || "",
          localidad: dd.address?.city || "",
          cp: dd.address?.zipCode || "",
          email: dd.contact?.email || ingressDetail.email || "",
        },
        parentSku: movementDetail.sku_id || movementDetail.product_id || "",
        displayName: movementDetail.description || "",
        width: pkg.width || 0,
        height: pkg.height || 0,
        depth: pkg.depth || 0,
        total_weight: pkg.total_weight || 0,
        declared_value: pkg.declared_value || 0,
        barcode: `${ingressDetail.id}-B${index + 1}`,
        remitente,
      };
    });

    setLabelsToPrint(labels);
  };

  // Descarga la etiqueta del canal (ML ME2/Flex) on-demand, igual que el listado.
  const handleDownloadMlLabel = async () => {
    const orderId = ingressDetail?.integration?.transaction_id;
    if (!orderId) return;
    try {
      const blob = await integrationService.getMercadoLibreLabel(orderId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `etiqueta_${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar la etiqueta de Mercado Libre:", error);
    }
  };

  // Envío convencional (red_despacho): baja la etiqueta de la PAQUETERÍA (carrier). Si el
  // envío todavía no tiene tracking, lo genera primero. El PDF trae una etiqueta por bulto.
  const handleDownloadCarrierLabel = async () => {
    const logisticsId = logistics?.id || ingressDetail?.movement_logistics_id;
    if (!logisticsId) return;
    try {
      if (!logistics?.trackingCode) {
        await integrationService.createLogisticsShipment(logisticsId);
      }
      const blob = await integrationService.getLogisticsLabel(logisticsId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `etiqueta_paqueteria_${ingressDetail?.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar la etiqueta de la paquetería:", error);
    }
  };

  // Reimpresión desde el detalle: baja la etiqueta que corresponde a la operativa
  // (etiqueta del canal para ML ME2/Flex; de la paquetería para red_despacho; de bulto
  // para el resto) y sella label_printed_at (por si se imprime por primera vez desde acá,
  // para ocultar el botón del listado). Siempre disponible en prepared/done: es la vía
  // de reimpresión.
  const mlLabeled =
    isMercadoLibreIntegration(ingressDetail) &&
    isMlShippingMode(shippingMethod || ingressDetail?.shipping_method);
  const isRedDespacho =
    (shippingMethod || ingressDetail?.shipping_method) === "red_despacho";
  const handleReprintLabel = async () => {
    if (mlLabeled) {
      await handleDownloadMlLabel();
    } else if (isRedDespacho) {
      await handleDownloadCarrierLabel();
    } else {
      handlePrintLabel();
    }
    if (ingressDetail?.id && typeof onLabelPrinted === "function") {
      onLabelPrinted(ingressDetail.id);
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString();

    const optimizedLogo = await optimizeImage(LogoPdf, {
      maxWidth: 300,
      maxHeight: 240,
      quality: 0.8,
    });

    doc.setFont("Helvetica");

    const logoWidth = 25;
    const logoHeight = 20;
    doc.addImage(
      optimizedLogo,
      "PNG",
      170,
      10,
      logoWidth,
      logoHeight,
      undefined,
      "FAST"
    );

    doc.setFontSize(20);
    doc.setFont("Helvetica", "bold");
    doc.text("DETALLE DE EGRESO", 105, 20, { align: "center" });

    let verticalSpace = 55;

    doc.setFontSize(12);
    doc.setFont("Helvetica", "normal");
    // Remitente = depósito de origen (configurable en /profile).
    const remitenteLines = warehouseRemitenteLines(ingressDetail);
    let headerY = 30;
    (remitenteLines.length ? remitenteLines : [""]).slice(0, 3).forEach((ln) => {
      doc.text(ln, 105, headerY, { align: "center" });
      headerY += 5;
    });

    doc.line(15, 45, 195, 45);

    doc.setFontSize(10);

    doc.setFont("Helvetica", "bold");
    doc.text("Información del egreso", 20, verticalSpace);

    doc.setFont("Helvetica", "normal");
    verticalSpace += 8;

    doc.text(`Fecha de Creación: ${currentDate}`, 20, verticalSpace);
    doc.text(`N° egreso: ${ingressDetail.id}`, 110, verticalSpace);

    if (ingressDetail.integration) {
      verticalSpace += 5;
      doc.text(`Canal: ${formatChannel(ingressDetail.integration.name)}`, 20, verticalSpace);
      const saleRef = formatSaleRef(ingressDetail.integration);
      if (saleRef) doc.text(`N° orden: ${saleRef}`, 110, verticalSpace);
    }

    verticalSpace += 5;
    const clientName = ingressDetail.provider_name || logistics?.deliveryDetails?.receiverName || "-";
    const clientPhone = ingressDetail.phone_number || logistics?.deliveryDetails?.contact?.phone || "-";
    const clientDni = logistics?.deliveryDetails?.receiverId || "";
    const shippingMethod = logistics?.shippingMethod || null;

    doc.text(`Cliente: ${clientName}`, 20, verticalSpace);
    doc.text(`Teléfono: ${clientPhone}`, 110, verticalSpace);

    verticalSpace += 5;
    if (clientDni) {
      doc.text(`DNI: ${clientDni}`, 20, verticalSpace);
    }
    if (shippingMethod) {
      doc.text(`Tipo de retiro: ${formatShippingMethod(shippingMethod)}`, 110, verticalSpace);
    }
    if (!clientDni && !shippingMethod) {
      verticalSpace -= 5;
    }

    verticalSpace += 5;
    doc.text(`Depósito: ${ingressDetail.warehouse_name || ingressDetail.warehouse_id || "-"}`, 20, verticalSpace);

    verticalSpace += 5;
    if (deliveryDetails?.address?.street) {
      const addrLine = `${deliveryDetails.address.street}${deliveryDetails.address.streetNumber ? ` ${deliveryDetails.address.streetNumber}` : ""
        }${deliveryDetails.address.floor ? `, ${deliveryDetails.address.floor}` : ""}`;
      doc.text(`Dirección: ${addrLine}`, 20, verticalSpace);
      verticalSpace += 5;
    }
    if (deliveryDetails?.address?.city) {
      const cityLine = `${deliveryDetails.address.city}${deliveryDetails.address.zipCode ? ` (CP: ${deliveryDetails.address.zipCode})` : ""
        }`;
      doc.text(`Localidad: ${cityLine}`, 20, verticalSpace);
      verticalSpace += 5;
    }

    const totalBultos =
      typeof ingressDetail?.package_count === "number" && ingressDetail.package_count > 0
        ? ingressDetail.package_count
        : ingressDetail?.packages?.length || 0;
    doc.text(`Cantidad de bultos: ${totalBultos}`, 20, verticalSpace);

    verticalSpace += 5;

    doc.text(`Email: ${ingressDetail.email}`, 20, verticalSpace);

    verticalSpace += 6;
    doc.line(15, verticalSpace, 195, verticalSpace);

    verticalSpace += 10;
    const data = ingressDetail?.movement_details?.map((product) => [
      product.sku_id,
      product.description,
      product.original_count,
    ]);

    autoTable(doc, {
      startY: verticalSpace,
      head: [["SKU", "Descripción", "Cantidad"]],
      body: data,
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 115 },
        2: { cellWidth: 25 },
      },
      styles: {
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
        fontSize: 10,
        cellPadding: 3,
      },
    });

    verticalSpace = doc.lastAutoTable.finalY + 10;
    const totalUnidadesIngresadas = ingressDetail?.movement_details?.reduce(
      (total, producto) => total + (producto.original_count || 0),
      0
    );
    doc.setFont("Helvetica", "bold");
    doc.text(
      `Total de productos: ${ingressDetail?.movement_details?.length || 0}`,
      20,
      verticalSpace
    );
    verticalSpace += 5;
    doc.text(
      `Total de unidades: ${totalUnidadesIngresadas}`,
      20,
      verticalSpace
    );

    verticalSpace += 10;
    // Agregar información de bultos si existe
    if (shippingMethod === "red_despacho" && ingressDetail?.packages && ingressDetail.packages.length > 0) {
      verticalSpace += 10;
      doc.line(15, verticalSpace, 195, verticalSpace);
      verticalSpace += 8;
      doc.setFont("Helvetica", "bold");
      doc.text("Información de Bultos", 20, verticalSpace);
      doc.setFont("Helvetica", "normal");
      verticalSpace += 8;

      const packagesData = ingressDetail.packages.map((pkg, idx) => [
        pkg.index || idx + 1,
        `${pkg.width || "-"} x ${pkg.height || "-"} x ${pkg.depth || "-"} cm`,
        `${pkg.total_weight || "-"} g`,
        `$${pkg.declared_value || "-"}`,
      ]);

      autoTable(doc, {
        startY: verticalSpace,
        head: [["Bulto", "Dimensiones", "Peso", "Valor Declarado"]],
        body: packagesData,
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 60 },
          2: { cellWidth: 40 },
          3: { cellWidth: 45 },
        },
        styles: {
          overflow: "linebreak",
          halign: "center",
          valign: "middle",
          fontSize: 9,
          cellPadding: 2,
        },
      });
    }

    const pdfShippingMethod = logistics?.shippingMethod || null;

    let sectionY = (ingressDetail?.packages?.length > 0)
      ? doc.lastAutoTable.finalY + 12
      : verticalSpace + 5;

    const ensurePageForSection = (minY) => {
      if (minY > 250) {
        doc.addPage();
        return 20;
      }
      return minY;
    };

    const hasRedespachoTransport =
      deliveryDetails?.thirdPartyCarrier &&
      (deliveryDetails.thirdPartyCarrier.intermediate?.companyName ||
        deliveryDetails.thirdPartyCarrier.final?.companyName);

    if (pdfShippingMethod === "redespacho" && hasRedespachoTransport) {
      const carrier = deliveryDetails.thirdPartyCarrier;
      sectionY = ensurePageForSection(sectionY);
      doc.line(15, sectionY - 4, 195, sectionY - 4);
      doc.setFont("Helvetica", "bold");
      doc.text("Información de Transporte", 20, sectionY);
      doc.setFont("Helvetica", "normal");
      sectionY += 8;

      if (carrier.intermediate?.companyName) {
        doc.setFont("Helvetica", "bold");
        doc.text("Transporte Intermedio", 20, sectionY);
        sectionY += 6;
        doc.setFont("Helvetica", "normal");
        doc.text(`Empresa: ${carrier.intermediate.companyName}`, 20, sectionY);
        sectionY += 5;
        if (carrier.intermediate.licensePlate) {
          doc.text(`Patente: ${carrier.intermediate.licensePlate}`, 20, sectionY);
          sectionY += 5;
        }
        if (carrier.intermediate.phone) {
          doc.text(`Tel: ${carrier.intermediate.phone}`, 20, sectionY);
          sectionY += 5;
        }
        sectionY += 4;
      }

      if (carrier.final?.companyName) {
        sectionY = ensurePageForSection(sectionY);
        doc.setFont("Helvetica", "bold");
        doc.text("Transporte Final", 20, sectionY);
        sectionY += 6;
        doc.setFont("Helvetica", "normal");
        doc.text(`Empresa: ${carrier.final.companyName}`, 20, sectionY);
        sectionY += 5;
        if (carrier.final.licensePlate) {
          doc.text(`Patente: ${carrier.final.licensePlate}`, 20, sectionY);
          sectionY += 5;
        }
        if (carrier.final.phone) {
          doc.text(`Tel: ${carrier.final.phone}`, 20, sectionY);
          sectionY += 5;
        }
      }
    }

    if (pdfShippingMethod === "red_despacho" && deliveryDetails?.quote?.carrierName) {
      const q = deliveryDetails.quote;
      sectionY = ensurePageForSection(sectionY);
      doc.line(15, sectionY - 4, 195, sectionY - 4);
      doc.setFont("Helvetica", "bold");
      doc.text("Cotización de Envío", 20, sectionY);
      sectionY += 6;
      doc.setFont("Helvetica", "normal");
      doc.text(`Transportista: ${q.carrierName}`, 20, sectionY);
      sectionY += 5;
      if (q.price != null && q.price !== "") {
        doc.text(`Precio: $${Number(q.price).toLocaleString()}`, 20, sectionY);
        sectionY += 5;
      }
      if (q.estimatedDays != null && q.estimatedDays !== "") {
        doc.text(`Tiempo estimado: ${q.estimatedDays} días`, 20, sectionY);
      }
    }

    doc.save(`Detalle_Egreso_${ingressDetail.id || "sin_id"}.pdf`);
  };

  const optimizeImage = (imageSource, options = {}) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");

        let width = img.width;
        let height = img.height;

        if (options.maxWidth && width > options.maxWidth) {
          height = (options.maxWidth * height) / width;
          width = options.maxWidth;
        }

        if (options.maxHeight && height > options.maxHeight) {
          width = (options.maxHeight * width) / height;
          height = options.maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        resolve(canvas.toDataURL("image/png", options.quality || 0.8));
      };
      img.src = imageSource;
    });
  };

  const headerCellSx = {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "#475569",
    backgroundColor: "#f1f5f9",
    borderBottom: "2px solid #e2e8f0",
    py: 1.5,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pb: 0.5,
          pt: 2.5,
          px: 3,
        }}
      >
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Detalle del egreso
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "text.secondary", mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
        <Box
          sx={{
            backgroundColor: "#f8fafc",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            p: 1.75,
            mb: 1.5,
          }}
        >
          <Grid container spacing={0} columnSpacing={4}>
            <Grid item xs={12} md={6}>
              <DetailGroup icon={<ReceiptIcon fontSize="small" />} title="Orden">
                <DetailField label="Fecha creación" value={formatDateString(ingressDetail?.date_created)} />
                <DetailField label="N° egreso" value={ingressDetail?.id} />
                {ingressDetail?.integration && (
                  <>
                    <DetailField label="Canal de venta" value={formatChannel(ingressDetail.integration.name)} />
                    <DetailField label="N° de orden" value={formatSaleRef(ingressDetail.integration)} />
                    {trackingLabel(logistics?.channelStatus, logistics?.channelSubstatus) && (
                      <DetailField
                        label="Seguimiento del envío"
                        value={trackingLabel(logistics?.channelStatus, logistics?.channelSubstatus)}
                      />
                    )}
                    {logistics?.trackingCode && (
                      <DetailField label="N° de tracking" value={logistics.trackingCode} />
                    )}
                  </>
                )}
              </DetailGroup>

              <DetailGroup icon={<LocalShippingIcon fontSize="small" />} title="Operativa">
                <DetailField label="Operativa" value={formatOperativa(shippingMethod)} />
                {shippingMethod === "transporte_manual" && (
                  <DetailField
                    label="Transporte"
                    value={
                      deliveryDetails?.thirdPartyCarrier?.intermediate?.companyName ||
                      deliveryDetails?.thirdPartyCarrier?.intermediate?.company_name ||
                      "—"
                    }
                  />
                )}
                <DetailField label="Cantidad de bultos" value={totalBultos} />
                <DetailField label="Depósito" value={ingressDetail?.warehouse_name || ingressDetail?.warehouse_id || "—"} />
              </DetailGroup>
            </Grid>

            <Grid item xs={12} md={6}>
              <DetailGroup icon={<BusinessIcon fontSize="small" />} title="Cliente">
                <DetailField label="Nombre" value={ingressDetail?.provider_name || deliveryDetails?.receiverName || "—"} />
                {deliveryDetails?.receiverId && (
                  <DetailField label="DNI" value={deliveryDetails.receiverId} />
                )}
                <DetailField label="Teléfono" value={ingressDetail?.phone_number || deliveryDetails?.contact?.phone || "—"} />
                <DetailField label="Email" value={ingressDetail?.email || deliveryDetails?.contact?.email || "—"} />
                {deliveryDetails?.address?.street && (
                  <DetailField
                    label="Dirección"
                    value={`${deliveryDetails.address.street}${deliveryDetails.address.streetNumber ? ` ${deliveryDetails.address.streetNumber}` : ""}${deliveryDetails.address.floor ? `, ${deliveryDetails.address.floor}` : ""}`}
                  />
                )}
                {deliveryDetails?.address?.city && (
                  <DetailField
                    label="Localidad"
                    value={`${deliveryDetails.address.city}${deliveryDetails.address.zipCode ? ` (CP: ${deliveryDetails.address.zipCode})` : ""}`}
                  />
                )}
              </DetailGroup>
            </Grid>
          </Grid>
        </Box>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: "#334155" }}>
          Productos
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            overflow: "auto",
            maxHeight: 300,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>SKU</TableCell>
                <TableCell sx={headerCellSx}>Descripción</TableCell>
                <TableCell align="center" sx={headerCellSx}>Cantidad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ingressDetail?.movement_details?.map((producto, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ py: 1.2 }}>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        backgroundColor: "#E8F0FE",
                        color: "#3949ab",
                        px: 1.2,
                        py: 0.3,
                        borderRadius: "6px",
                      }}
                    >
                      {producto.sku_id || producto.product_id || "-"}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.2 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {producto.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.2 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {producto.original_count || 0}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Sección de Bultos */}
        {shippingMethod === "red_despacho" && packages && packages.length > 0 && (
          <Box sx={{ marginTop: 3, px: 2, pb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Bultos ({packages.length})
            </Typography>
            <Grid container spacing={2}>
              {packages.map((pkg, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, borderColor: "#e2e8f0" }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Bulto {pkg.index || index + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ancho: {pkg.width || "-"} cm
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Alto: {pkg.height || "-"} cm
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Profundidad: {pkg.depth || "-"} cm
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Peso: {pkg.total_weight || "-"} g
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Valor declarado: ${pkg.declared_value || "-"}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Información de Transporte (si aplica) */}
        {shippingMethod === "redespacho" && deliveryDetails?.thirdPartyCarrier && (
          <Box sx={{ marginTop: 2, px: 2, pb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Información de Transporte
            </Typography>
            <Grid container spacing={2}>
              {deliveryDetails.thirdPartyCarrier.intermediate?.companyName && (
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Transporte Intermedio
                    </Typography>
                    <Typography variant="body2">
                      Empresa: {deliveryDetails.thirdPartyCarrier.intermediate.companyName}
                    </Typography>
                    {deliveryDetails.thirdPartyCarrier.intermediate.licensePlate && (
                      <Typography variant="body2">
                        Patente: {deliveryDetails.thirdPartyCarrier.intermediate.licensePlate}
                      </Typography>
                    )}
                    {deliveryDetails.thirdPartyCarrier.intermediate.phone && (
                      <Typography variant="body2">
                        Tel: {deliveryDetails.thirdPartyCarrier.intermediate.phone}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              )}
              {deliveryDetails.thirdPartyCarrier.final?.companyName && (
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Transporte Final
                    </Typography>
                    <Typography variant="body2">
                      Empresa: {deliveryDetails.thirdPartyCarrier.final.companyName}
                    </Typography>
                    {deliveryDetails.thirdPartyCarrier.final.licensePlate && (
                      <Typography variant="body2">
                        Patente: {deliveryDetails.thirdPartyCarrier.final.licensePlate}
                      </Typography>
                    )}
                    {deliveryDetails.thirdPartyCarrier.final.phone && (
                      <Typography variant="body2">
                        Tel: {deliveryDetails.thirdPartyCarrier.final.phone}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Cotización (si aplica) */}
        {shippingMethod === "red_despacho" && deliveryDetails?.quote?.carrierName && (
          <Box sx={{ marginTop: 2, px: 2, pb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                Cotización de Envío
              </Typography>
              <Typography variant="body2">
                Transportista: {deliveryDetails.quote.carrierName}
              </Typography>
              {deliveryDetails.quote.price && (
                <Typography variant="body2">
                  Precio: ${deliveryDetails.quote.price.toLocaleString()}
                </Typography>
              )}
              {deliveryDetails.quote.estimatedDays && (
                <Typography variant="body2">
                  Tiempo estimado: {deliveryDetails.quote.estimatedDays} días
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5 }}>
        {(ingressDetail?.status === "prepared" || ingressDetail?.status === "done") && (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReprintLabel}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Reimprimir etiqueta
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={generatePDF}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          Descargar PDF
        </Button>
      </DialogActions>
      {labelsToPrint.length > 0 && (
        <EgressLabelPrinter
          labels={labelsToPrint}
          onPrintComplete={() => setLabelsToPrint([])}
        />
      )}
    </Dialog>
  );
};

export default ModalView;
